import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { finalize, Observable } from 'rxjs';
import { SubscriptionResponse, SubscriptionService } from '../../../core/services/subscriptions/subscription.service';
import { subscriptionIntervalLabel } from './subscription-interval';
import {
	SUBSCRIPTION_PAYMENT_STATUS_KEYS,
	SUBSCRIPTION_STATUS_CLASSES,
	SUBSCRIPTION_STATUS_KEYS,
	subscriptionLabel,
} from './subscription-status';

@Component({
	selector: 'app-subscription-list',
	imports: [SharedModule, ButtonComponent, SpinnerComponent],
	templateUrl: './subscription-list.component.html',
	styleUrl: './subscription-list.component.scss',
})
export class SubscriptionListComponent implements OnInit {
	subscriptions: SubscriptionResponse[] = [];
	loading = false;
	loadError = false;
	/** Subscription whose action is in flight (its buttons are disabled) */
	busyId: number | null = null;
	readonly isAdmin: boolean;
	total = 0;
	page = 1;
	limit = 10;

	constructor(
		private subscriptionService: SubscriptionService,
		private messageAlertService: MessageAlertService,
		private translocoService: TranslocoService,
		private confirmDialogService: ConfirmDialogService,
		currentUserService: CurrentUserService,
	) {
		// Manual payments are approved with requests:approve (same check as the backend)
		this.isAdmin = currentUserService.hasPermissionOrAdmin('requests:approve');
	}

	ngOnInit() {
		this.loadSubscriptions();
	}

	loadSubscriptions() {
		this.loading = true;
		this.loadError = false;
		this.subscriptionService
			.getSubscriptions({
				page: this.page.toString(),
				limit: this.limit.toString(),
				sort_by: 'created_at',
				sort_order: 'desc',
			})
			.subscribe({
				next: (response) => {
					this.subscriptions = response.data;
					this.total = response.total;
					this.loading = false;
				},
				error: () => {
					this.loading = false;
					this.loadError = true;
				},
			});
	}

	/** The status the customer should read: an unpaid subscription is "awaiting payment", not "active" */
	getDisplayStatus(sub: SubscriptionResponse): string {
		return sub.status === 'ACTIVE' && !this.isPaid(sub) ? 'PENDING_PAYMENT' : sub.status;
	}

	getStatusLabel(status: string): string {
		return subscriptionLabel(SUBSCRIPTION_STATUS_KEYS, status, this.translocoService);
	}

	getStatusClass(status: string): string {
		return SUBSCRIPTION_STATUS_CLASSES[status] || '';
	}

	getPaymentStatusLabel(status: string): string {
		return subscriptionLabel(SUBSCRIPTION_PAYMENT_STATUS_KEYS, status, this.translocoService);
	}

	getIntervalLabel(minutes: number): string {
		return subscriptionIntervalLabel(minutes, this.translocoService);
	}

	get pages(): number {
		return Math.max(1, Math.ceil(this.total / this.limit));
	}

	goToPage(page: number) {
		if (page < 1 || page > this.pages || page === this.page) return;
		this.page = page;
		this.loadSubscriptions();
	}

	/** Paid? An unpaid subscription is not really active: no deliveries, no pause/cancel */
	isPaid(sub: SubscriptionResponse): boolean {
		return sub.paymentStatus === 'COMPLETED';
	}

	/** Manual payment (Revolut/Bizum) waiting for an admin */
	awaitsApproval(sub: SubscriptionResponse): boolean {
		return (
			this.isAdmin &&
			!!sub.paymentMethod &&
			['PENDING_PAYMENT', 'PENDING_VERIFICATION'].includes(sub.paymentStatus) &&
			sub.status !== 'CANCELLED'
		);
	}

	pause(sub: SubscriptionResponse) {
		this.run(
			sub,
			this.subscriptionService.pause(sub.id),
			'subscriptions.messages.pausedTitle',
			'subscriptions.messages.pausedMessage',
			'subscriptions.messages.pauseError',
		);
	}

	resume(sub: SubscriptionResponse) {
		this.run(
			sub,
			this.subscriptionService.resume(sub.id),
			'subscriptions.messages.resumedTitle',
			'subscriptions.messages.resumedMessage',
			'subscriptions.messages.resumeError',
		);
	}

	/** Irreversible: asks first */
	cancel(sub: SubscriptionResponse) {
		this.confirmDialogService.confirmAndExecute(
			'cancel',
			this.translocoService.translate('subscriptions.list.cancelTarget'),
			() => this.withBusy(sub, this.subscriptionService.cancel(sub.id)),
			{
				rejectLabel: this.translocoService.translate('common.actions.back'),
				successMessage: this.translocoService.translate('subscriptions.messages.cancelledMessage'),
				onSuccess: () => this.loadSubscriptions(),
			},
		);
	}

	approvePayment(sub: SubscriptionResponse) {
		this.confirmDialogService.confirmAndExecute(
			'approve',
			this.translocoService.translate('subscriptions.list.approveTarget'),
			() => this.withBusy(sub, this.subscriptionService.approvePayment(sub.id)),
			{
				successMessage: this.translocoService.translate('subscriptions.messages.approvedMessage'),
				onSuccess: () => this.loadSubscriptions(),
			},
		);
	}

	rejectPayment(sub: SubscriptionResponse) {
		this.confirmDialogService.confirmAndExecute(
			'reject',
			this.translocoService.translate('subscriptions.list.approveTarget'),
			() => this.withBusy(sub, this.subscriptionService.rejectPayment(sub.id)),
			{
				successMessage: this.translocoService.translate('subscriptions.messages.rejectedMessage'),
				onSuccess: () => this.loadSubscriptions(),
			},
		);
	}

	/** One action at a time per card: the buttons stay disabled until the answer arrives */
	private withBusy<T>(sub: SubscriptionResponse, request: Observable<T>): Observable<T> {
		this.busyId = sub.id;
		return request.pipe(
			finalize(() => {
				this.busyId = null;
			}),
		);
	}

	private run(
		sub: SubscriptionResponse,
		request: Observable<unknown>,
		titleKey: string,
		messageKey: string,
		errorKey: string,
	) {
		if (this.busyId !== null) return;
		this.withBusy(sub, request).subscribe({
			next: () => {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate(titleKey),
					this.translocoService.translate(messageKey),
				);
				this.loadSubscriptions();
			},
			// errors.<code> says why; the generic one is the fallback
			error: (error: unknown) =>
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					translateApiError(error, this.translocoService, errorKey),
				),
		});
	}
}
